import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, EyeOff, ChevronDown, Search, Check } from "lucide-react";
import { COUNTRY_OPTIONS, findCountry } from "./countries";

/**
 * SearchableCountrySelect — ISO-3166-1 dropdown with flag + RU name + autocomplete.
 *
 * Props:
 *   value:    ISO-2 code (e.g. "KZ"). Use "" for unselected.
 *   onChange: (code) => void
 *   testId:   data-testid prefix for the trigger button
 *   placeholder
 */
export function SearchableCountrySelect({ value, onChange, testId = "country-select", placeholder = "Страна проживания" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef();
  const inputRef = useRef();
  const selected = value ? findCountry(value) : null;

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    const off = (e) => { if (!ref.current?.contains(e.target)) { setOpen(false); setQuery(""); } };
    document.addEventListener("click", off);
    return () => document.removeEventListener("click", off);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter((c) =>
      c.ru.toLowerCase().includes(q) ||
      c.en.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div ref={ref} style={{ position: "relative", marginBottom: 12 }}>
      <button type="button" onClick={() => setOpen(!open)}
        data-testid={`${testId}-trigger`}
        className="auth-input"
        style={{
          paddingLeft: 14, display: "flex", alignItems: "center", gap: 10,
          width: "100%", cursor: "pointer", textAlign: "left",
          color: selected ? "var(--tg-text)" : "var(--tg-text-hint)",
        }}>
        {selected ? (
          <>
            <span style={{ fontSize: 20, lineHeight: 1 }}>{selected.flag || "🏳️"}</span>
            <span style={{ flex: 1 }}>{selected.ru} <span style={{ color: "var(--tg-text-hint)", fontSize: 12 }}>({selected.code})</span></span>
          </>
        ) : (
          <span style={{ flex: 1 }}>{placeholder}</span>
        )}
        <ChevronDown size={14} style={{ color: "var(--tg-text-hint)", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
      {open && (
        <div data-testid={`${testId}-dropdown`}
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
            background: "#111118", border: "1px solid var(--tg-border)",
            borderRadius: 12, padding: 8, maxHeight: 340, overflowY: "auto",
            boxShadow: "0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,242,255,0.08)",
            backdropFilter: "none",
          }}>
          <div style={{ position: "relative", marginBottom: 6 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--tg-text-hint)" }} />
            <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск страны..." data-testid={`${testId}-search`}
              style={{
                width: "100%", padding: "8px 10px 8px 32px", borderRadius: 8,
                background: "#0B0B10", border: "1px solid var(--tg-border)",
                color: "var(--tg-text)", fontSize: 13,
              }} />
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--tg-text-hint)", textAlign: "center" }}>
              Ничего не найдено
            </div>
          )}
          {filtered.map((c) => (
            <button key={c.code} type="button" onClick={() => { onChange(c.code); setOpen(false); setQuery(""); }}
              data-testid={`${testId}-option-${c.code}`}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "8px 10px", borderRadius: 6, border: "none",
                background: c.code === value ? "rgba(0,242,255,0.08)" : "transparent",
                color: c.code === value ? "var(--tg-blue)" : "var(--tg-text)",
                cursor: "pointer", textAlign: "left", fontSize: 13,
              }}
              onMouseEnter={(e) => { if (c.code !== value) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
              onMouseLeave={(e) => { if (c.code !== value) e.currentTarget.style.background = "transparent"; }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>{c.flag}</span>
              <span style={{ flex: 1 }}>{c.ru}</span>
              <span style={{ color: "var(--tg-text-hint)", fontSize: 11 }}>{c.code}</span>
              {c.code === value && <Check size={14} style={{ color: "var(--tg-blue)" }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PasswordInput — eye/eye-off toggle. Wraps the existing .auth-input style.
 *
 * Props:
 *   icon:        decorative left icon (e.g. <Lock />)
 *   value, onChange
 *   placeholder
 *   testId
 *   required
 */
export function PasswordInput({ icon, value, onChange, placeholder, testId, required = false }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", marginBottom: 12 }}>
      <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "var(--tg-text-hint)", zIndex: 2, pointerEvents: "none" }}>{icon}</span>
      <input className="auth-input" type={show ? "text" : "password"} placeholder={placeholder} value={value} required={required}
        onChange={(e) => onChange(e.target.value)} data-testid={testId}
        autoComplete="current-password"
        style={{ paddingRight: 50 }} />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Скрыть пароль" : "Показать пароль"}
        data-testid={`${testId}-toggle`}
        style={{
          position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
          width: 34, height: 34, borderRadius: 8,
          background: "rgba(0,0,0,0.45)",
          border: "1px solid rgba(255,255,255,0.12)",
          cursor: "pointer",
          color: "#F5F5FA",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 3,
          transition: "background .15s, border-color .15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,242,255,0.2)"; e.currentTarget.style.borderColor = "var(--tg-blue)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0.45)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}>
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
